import React, { useCallback, useEffect, useState } from "react";
import { usePageTitle } from "hooks";
import { Col, Row } from "antd";
import { AppSetting } from "utils/appSettings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useRbac } from "features/rbac/rbac";
import { ERbacPermissions } from "features/rbac/rbacPermissionsList";
import FilterDrawer from "utils/commonFilter/FilterDrawer";
import { actions } from "models/Slices/FilterSlice";
import { dataSource } from "models/Slices/GlobalSlice";
import { IPriceComparisonQueryParams } from "models/interfaces";
import PriceComparisonListings from "./PriceComparisonListings";

const defaultQueryParams: IPriceComparisonQueryParams = {
  page_size: AppSetting.PAGE_SIZE,
  page_index: AppSetting.DEFAULT_PAGE_INDEX,
  sort_column: "days_to_event",
  sort_order: "asc",
  search_text: null,
  risk_type: null,
  low_or_no_availability_listings_only: false,
  high_risk_listings_only: false,
  show_broadcasted_listings: true,
};

const PriceComparisonPage: React.FC = () => {
  usePageTitle("Price Comparison");
  const { hasPermissions } = useRbac();
  const dispatch = useAppDispatch();
  const DataSource = useAppSelector(dataSource);
  const [search, setSearch] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [queryParams, setQueryParams] =
    useState<IPriceComparisonQueryParams>(defaultQueryParams);
  const [filtersAppliedWithValues, setFiltersAppliedWithValues] =
    useState<any[]>([]);

  useEffect(() => {
    dispatch(actions.getAllStatesData({ env: DataSource }));
    if (hasPermissions([ERbacPermissions.FILTER_BROKER_USER_SELECTION_ALL])) {
      dispatch(actions.getBrokerUserData({ env: DataSource }));
    }
    if (
      hasPermissions([
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
      ])
    ) {
      dispatch(actions.getListerUserData({ env: DataSource }));
    }
  }, []);

  const onApplyFilterWithValuesHandler = useCallback((e: any) => {
    setFiltersAppliedWithValues(e);
  }, []);

  const handleSearch = (e: any) => {
    e.preventDefault();
    setSearchQuery(search);
  };

  const closeSearch = () => {
    setSearch("");
    setSearchQuery("");
  };

  const filterChanged = (filters: any) => {
    setQueryParams({
      ...queryParams,
      ...filters,
      page_index: AppSetting.DEFAULT_PAGE_INDEX,
    });
  };

  return (
    <React.Fragment>
      <div className="mainHeading">
        <Row className="flx-1">
          <Col xs={18} sm={8} md={8} xl={5} className="mb12">
            <h2 id="priceComparisonTables">Price Comparison</h2>
          </Col>
          <Col xs={24} sm={12} md={12} xl={7}>
            <div className="search">
              <form onSubmit={handleSearch}>
                <input
                  type="search"
                  placeholder="Search by Event, Venue, Broker, Lister, Po no..."
                  value={search ? search : ""}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && search.length > 0 && (
                  <span onClick={closeSearch}>&times;</span>
                )}
              </form>
            </div>
          </Col>
          <Col xs={24} md={24} xl={12}>
            <ul className="innerHeaderRight">
              <li>
                <FilterDrawer
                  onApplyFilter={filterChanged}
                  onApplyFilterWithValues={onApplyFilterWithValuesHandler}
                  defaultAndValues={{
                    preFilledValues: { ...queryParams },
                  }}
                  showEventDatePicker={true}
                  showListingDatePicker={true}
                  showBrokerUserDropdown={hasPermissions([
                    ERbacPermissions.FILTER_BROKER_USER_SELECTION_ALL,
                  ])}
                  brokerUserDropdownType="single"
                  showListerDropdown={hasPermissions([
                    ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
                    ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
                  ])}
                  listerDropdownType="single"
                  showStateDropdown={true}
                  stateDropdownType="single"
                  showPricingIssuesCheckbox={true}
                  showTbPlacementCheckbox={true}
                  showPendingFillsCheckbox={true}
                  showHighRiskListingsOnlyCheckbox={true}
                  showLowAvailabilityListingsOnlyCheckbox={true}
                  showBroadcastedCheckbox={true}
                  showMissingInventoryTagCheckbox={hasPermissions([
                    ERbacPermissions.FILTER_SHOW_MISSING_INVENTORY_TAG,
                  ])}
                />
              </li>
            </ul>
          </Col>
        </Row>
      </div>
      <PriceComparisonListings
        searchQuery={searchQuery}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        filtersAppliedWithValues={filtersAppliedWithValues}
      />
    </React.Fragment>
  );
};

export default PriceComparisonPage;
